
import { apiClient } from '@/api/client';
import { CampaignData } from '@/hooks/sms/campaign/types';
import * as SmsServiceModule from '@/api/sms';
import { supabase } from '@/integrations/supabase/client';

// Get the default export from the SmsService module
const SmsService = SmsServiceModule.default;

// Define response interface for audience size
export interface AudienceSizeResponse {
  success: boolean;
  size: number;
  sample: any[];
  filters?: {
    visitFrequency: any[];
    lastVisit: any[];
    orderValue: any[];
    loyaltyTier: any[];
  };
  audienceType?: string;
  requestId?: string;
  note?: string;
}

// Function to get audience size estimates based on filters
export const getAudienceSize = async (restaurantIds: string[], filters: any): Promise<AudienceSizeResponse> => {
  try {
    // Enhanced debug logging
    console.log('[SMS-DEBUG] getAudienceSize called with:', JSON.stringify({
      restaurantIdsType: typeof restaurantIds,
      isArray: Array.isArray(restaurantIds),
      restaurantIdsLength: restaurantIds?.length || 0,
      restaurantIds,
      filters
    }));
    
    // Enhanced validation for restaurantIds to ensure it's always a valid array
    const safeRestaurantIds = Array.isArray(restaurantIds) ? restaurantIds : [];
    
    console.log('[SMS-DEBUG] Prepared API payload:', JSON.stringify({
      restaurantIds: safeRestaurantIds,
      ...filters
    }));
    
    try {
      // Try primary endpoint first
      console.log('[SMS-DEBUG] Trying primary endpoint /api/sms/audience/size');
      const response = await apiClient.post('/api/sms/audience/size', {
        restaurantIds: safeRestaurantIds,
        ...filters
      });
      
      console.log('[SMS-DEBUG] Primary endpoint success:', {
        status: response.status,
        hasData: !!response.data,
        dataType: typeof response.data
      });
      
      // Validate response data format
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format from primary endpoint');
      }
      
      return response.data;
    } catch (primaryError) {
      console.warn('[SMS-DEBUG] Primary endpoint failed:', primaryError);
      
      // Try v2 endpoint if primary fails
      try {
        console.log('[SMS-DEBUG] Trying fallback endpoint /api/sms/audience-size-v2');
        const v2Response = await apiClient.post('/api/sms/audience-size-v2', {
          restaurantIds: safeRestaurantIds,
          ...filters
        });
        
        console.log('[SMS-DEBUG] V2 endpoint success:', {
          status: v2Response.status,
          hasData: !!v2Response.data,
          dataType: typeof v2Response.data
        });
        
        return v2Response.data;
      } catch (v2Error) {
        console.warn('[SMS-DEBUG] V2 endpoint failed:', v2Error);
        
        // Try test endpoint as last resort
        console.log('[SMS-DEBUG] Trying test endpoint /api/sms/audience-size-test');
        const testResponse = await apiClient.post('/api/sms/audience-size-test', {
          restaurantIds: safeRestaurantIds,
          ...filters
        });
        
        console.log('[SMS-DEBUG] Test endpoint responded');
        return testResponse.data;
      }
    }
  } catch (error) {
    console.error('[SMS-DEBUG] Error fetching audience size:', error);
    
    // Return mock data for development when API is unavailable
    if (process.env.NODE_ENV === 'development') {
      console.log('Using mock audience data due to API error');
      return {
        success: true,
        size: filters.audienceType === 'all' ? 150 : 75,
        sample: [],
        filters: filters.filters || {}
      };
    }
    
    throw error;
  }
};

// Function to explicitly call the v2 endpoint
export const calculateAudienceSizeV2 = async (restaurantIds: string[], filters: any): Promise<AudienceSizeResponse> => {
  try {
    const safeRestaurantIds = Array.isArray(restaurantIds) ? restaurantIds : [];
    
    console.log('[SMS-DEBUG] Calling V2 endpoint directly');
    const response = await apiClient.post('/api/sms/audience-size-v2', {
      restaurantIds: safeRestaurantIds,
      ...filters
    });
    
    return response.data;
  } catch (error) {
    console.error('[SMS-DEBUG] Error calling V2 endpoint:', error);
    throw error;
  }
};

// Function to explicitly call the test endpoint
export const calculateAudienceSizeTest = async (restaurantIds: string[], filters: any): Promise<AudienceSizeResponse> => {
  try {
    const safeRestaurantIds = Array.isArray(restaurantIds) ? restaurantIds : [];
    
    console.log('[SMS-DEBUG] Calling test endpoint directly');
    const response = await apiClient.post('/api/sms/audience-size-test', {
      restaurantIds: safeRestaurantIds,
      ...filters
    });
    
    return response.data;
  } catch (error) {
    console.error('[SMS-DEBUG] Error calling test endpoint:', error);
    throw error;
  }
};

// Function to create a new campaign
export const createCampaign = async (campaignData: any) => {
  try {
    // Log the campaign data being sent to help debug filtered campaigns
    console.log('[SMS-CAMPAIGN] Creating campaign with data:', {
      campaign_name: campaignData.campaign_name,
      audience_type: campaignData.audience_type,
      has_audience_data: !!campaignData.audience_data,
      audience_data_length: campaignData.audience_data?.length || 0,
      audience_size: campaignData.audience_size,
      selected_restaurants: campaignData.selected_restaurants
    });

    const response = await apiClient.post('/api/sms/campaigns', campaignData);
    
    console.log('[SMS-CAMPAIGN] Campaign creation response:', {
      success: response.data?.success,
      campaign_id: response.data?.campaign_id,
      status: response.data?.status
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

// Function to load available templates
export const getTemplates = async (restaurantId: string) => {
  try {
    console.log(`Fetching templates for restaurant: ${restaurantId}`);
    // Convert to snake_case parameter to match backend expectations
    const response = await apiClient.get(`/api/sms/templates?restaurant_id=${restaurantId}`);

    // Transform the templates from snake_case to camelCase for frontend compatibility
    if (response.data && response.data.success && Array.isArray(response.data.templates)) {
      const transformedTemplates = response.data.templates.map(template => ({
        id: template.id.toString(),
        name: template.template_name,
        content: template.template_content,
        category: template.category || 'General',
        restaurant_id: template.restaurant_id,
        organisation_id: template.organisation_id,
        is_org_wide: !!template.organisation_id,
        lastUsed: template.updated_at || template.created_at,
        usageCount: template.estimated_units || 0,
        date: new Date(template.created_at).toLocaleDateString()
      }));

      console.log(`[SMS Templates] Transformed ${transformedTemplates.length} templates for frontend compatibility`);

      return {
        ...response.data,
        templates: transformedTemplates
      };
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching templates:', error);

    // Enhanced error logging to help diagnose issues
    if (error.response) {
      console.error('API error details:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }

    // Return mock templates for development when API is unavailable
    if (process.env.NODE_ENV === 'development') {
      console.log('Using mock templates data due to API error');
      return {
        templates: [
          { id: 'template1', name: 'Welcome Message', content: 'Welcome to {{restaurant_name}}! We\'re glad to have you as our customer.' },
          { id: 'template2', name: 'Special Offer', content: 'Don\'t miss our special offer: {{offer_details}}. Valid until {{valid_until}}.' },
          { id: 'template3', name: 'Event Invitation', content: 'You\'re invited to {{event_name}} at {{restaurant_name}} on {{event_date}}. We hope to see you there!' }
        ]
      };
    }

    throw error;
  }
};

// Function to create template
export const createTemplate = async (templateData: any) => {
  try {
    // Ensure template data is in the correct format (snake_case)
    const formattedData = {
      restaurant_id: templateData.restaurant_id || templateData.restaurantId,
      organisation_id: templateData.organisation_id || templateData.organisationId, // Include organisation_id
      template_name: templateData.template_name || templateData.templateName || templateData.name,
      template_content: templateData.template_content || templateData.templateContent || templateData.content,
      category: templateData.category || 'General',
      estimated_units: templateData.estimated_units || templateData.estimatedUnits // Include estimated_units
    };

    // Calculate estimated_units if not provided
    if (!formattedData.estimated_units && formattedData.template_content) {
      const content = formattedData.template_content;
      const hasUnicode = Array.from(content).some(char => char.charCodeAt(0) > 127);
      const charLimit = hasUnicode ? 70 : 160;
      formattedData.estimated_units = Math.ceil(content.length / charLimit) || 1;

      console.log('[SMS Templates] Calculated estimated_units:', {
        content_length: content.length,
        has_unicode: hasUnicode,
        char_limit: charLimit,
        estimated_units: formattedData.estimated_units
      });
    }

    // Filter out undefined values
    const finalFormattedData = Object.fromEntries(
      Object.entries(formattedData).filter(([_, value]) => value !== undefined)
    );

    // Log what we're sending to help with debugging
    console.log('[SMS Templates] Creating template with data:', {
      restaurant_id: finalFormattedData.restaurant_id,
      organisation_id: finalFormattedData.organisation_id,
      template_name: finalFormattedData.template_name,
      template_content: finalFormattedData.template_content ?
        `${finalFormattedData.template_content.substring(0, 30)}...` : null,
      category: finalFormattedData.category,
      estimated_units: finalFormattedData.estimated_units
    });

    // Validate required fields before sending request
    if (!finalFormattedData.restaurant_id || !finalFormattedData.template_name || !finalFormattedData.template_content) {
      const missingFields = [];
      if (!finalFormattedData.restaurant_id) missingFields.push('restaurant_id');
      if (!finalFormattedData.template_name) missingFields.push('template_name');
      if (!finalFormattedData.template_content) missingFields.push('template_content');

      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    const response = await apiClient.post('/api/sms/templates', finalFormattedData);

    console.log('[SMS Templates] Template created successfully:', {
      success: response.data?.success,
      templateId: response.data?.template?.id,
      status: response.status
    });

    return response.data;
  } catch (error) {
    console.error('Error creating template:', error);

    // Enhanced error logging
    if (error.response) {
      console.error('Server response error:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }

    // Provide more detailed error information in development
    if (process.env.NODE_ENV === 'development') {
      // Mock successful response for development without API
      // Calculate estimated_units if not provided for mock response
      let estimatedUnits = templateData.estimated_units || templateData.estimatedUnits;
      if (!estimatedUnits && (templateData.template_content || templateData.content)) {
        const content = templateData.template_content || templateData.content;
        const hasUnicode = Array.from(content).some(char => char.charCodeAt(0) > 127);
        const charLimit = hasUnicode ? 70 : 160;
        estimatedUnits = Math.ceil(content.length / charLimit) || 1;
      }

      return {
        success: true,
        template: {
          id: `template-${Date.now()}`,
          name: templateData.template_name || templateData.name,
          content: templateData.template_content || templateData.content,
          category: templateData.category || 'General',
          organisation_id: templateData.organisation_id || templateData.organisationId,
          estimated_units: estimatedUnits || 1,
          created_at: new Date().toISOString()
        },
        note: 'This is a mock response for development'
      };
    }

    throw error;
  }
};

// Function to update an existing template
export const updateTemplate = async (templateId: string, templateData: any) => {
  try {
    // Ensure template data is in the correct format (snake_case)
    const formattedData = {
      template_name: templateData.template_name || templateData.templateName || templateData.name,
      template_content: templateData.template_content || templateData.templateContent || templateData.content,
      category: templateData.category || templateData.category
    };
    
    // Only include fields that are actually provided
    const dataToSend = Object.fromEntries(
      Object.entries(formattedData).filter(([_, value]) => value !== undefined)
    );
    
    // Log what we're sending to help with debugging
    console.log(`[SMS Templates] Updating template ${templateId} with data:`, {
      template_name: dataToSend.template_name,
      template_content: dataToSend.template_content ? 
        `${dataToSend.template_content.substring(0, 30)}...` : undefined,
      category: dataToSend.category,
      fields_updated: Object.keys(dataToSend)
    });
    
    // Validate that we have at least one field to update
    if (Object.keys(dataToSend).length === 0) {
      throw new Error('No fields provided for update');
    }
    
    const response = await apiClient.put(`/api/sms/templates/${templateId}`, dataToSend);
    
    console.log(`[SMS Templates] Template ${templateId} updated successfully:`, {
      success: response.data?.success,
      status: response.status
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating template:', error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Server response error:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    
    // Provide mock response for development
    if (process.env.NODE_ENV === 'development') {
      return {
        success: true,
        template: {
          id: templateId,
          ...templateData,
          updated_at: new Date().toISOString()
        },
        note: 'This is a mock response for development'
      };
    }
    
    throw error;
  }
};

// Function to delete a template
export const deleteTemplate = async (templateId: string) => {
  try {
    const response = await apiClient.delete(`/api/sms/templates/${templateId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting template:', error);
    
    // Provide mock response for development
    if (process.env.NODE_ENV === 'development') {
      return {
        success: true,
        message: 'Template deleted successfully'
      };
    }
    
    throw error;
  }
};

// Function to fetch campaigns (alias for functionality in SmsService)
export const fetchCampaigns = async (restaurantId: string, organisationId?: string) => {
  try {
    if (!SmsService || typeof SmsService.fetchCampaigns !== 'function') {
      console.error('SmsService.fetchCampaigns is not available');
      
      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock campaign data due to missing SmsService');
        return [
          { 
            id: 'camp1', 
            name: 'Welcome Campaign', 
            status: 'active', 
            sentCount: 120, 
            deliveredCount: 115,
            createdAt: new Date().toISOString()
          },
          { 
            id: 'camp2', 
            name: 'Monthly Promotion', 
            status: 'scheduled', 
            sentCount: 0, 
            deliveredCount: 0,
            createdAt: new Date(Date.now() - 86400000).toISOString()
          }
        ];
      }
      
      throw new Error('SMS service unavailable');
    }
    return await SmsService.fetchCampaigns(restaurantId, organisationId);
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// Function to fetch templates (alias for backwards compatibility)
export const fetchTemplates = getTemplates;

// Function to send a test message
export const sendTestMessage = async (restaurantId: string, message: string, phoneNumber: string) => {
  try {
    const response = await apiClient.post('/api/sms/test-message', {
      restaurantId,
      message,
      phoneNumber
    });
    return response.data;
  } catch (error) {
    console.error('Error sending test message:', error);
    throw error;
  }
};

// Define a type that normalizes the different response formats
type SyncCustomersResponse = {
  success: boolean;
  sync_id?: string;
  syncedCount?: number;
  total_customers?: number;
};

// Function to sync customers to SMS system
export const syncCustomers = async (options: {
  organisationId?: string;
  restaurantIds?: string[];
  restaurantId?: string;
}): Promise<SyncCustomersResponse> => {
  try {
    if (!SmsService || typeof SmsService.syncCustomers !== 'function') {
      console.error('SmsService.syncCustomers is not available');
      
      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock sync response due to missing SmsService');
        const totalCustomers = Math.floor(Math.random() * 100) + 50;
        return { 
          success: true,
          syncedCount: totalCustomers,
          total_customers: totalCustomers
        };
      }
      
      throw new Error('SMS sync service unavailable');
    }
    const result = await SmsService.syncCustomers(options);
    
    // Create a normalized response object
    const normalizedResponse: SyncCustomersResponse = {
      success: result.success || false
    };
    
    // Add optional fields if they exist
    if ('sync_id' in result) {
      normalizedResponse.sync_id = result.sync_id;
    }
    
    // Handle the different ways the customer count can be returned
    if ('total_customers' in result && typeof result.total_customers === 'number') {
      normalizedResponse.total_customers = result.total_customers;
      // Also set syncedCount for backwards compatibility
      normalizedResponse.syncedCount = result.total_customers;
    } else if ('syncedCount' in result && typeof result.syncedCount === 'number') {
      normalizedResponse.syncedCount = result.syncedCount;
      // Also set total_customers for backwards compatibility
      normalizedResponse.total_customers = result.syncedCount;
    }
    
    return normalizedResponse;
  } catch (error) {
    console.error('Error syncing customers:', error);
    throw error;
  }
};

// Check SMS status 
export const checkSmsStatus = async (organisationId: string) => {
  try {
    console.log(`Checking SMS status for organisation: ${organisationId}`);
    const response = await apiClient.get(`/api/sms-billing/status/${organisationId}`);
    return response.data;
  } catch (error) {
    console.error('Error checking SMS status:', error);
    
    // Provide more detailed error information
    let errorMessage = 'Unknown error checking SMS status';
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || 'No error message provided'}`;
      
      // Handle specific status codes
      if (error.response.status === 429) {
        errorMessage = 'Rate limit exceeded. Please try again in a few minutes.';
      } else if (error.response.status === 403) {
        errorMessage = 'Permission denied. You do not have access to this resource.';
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = 'Network error: No response received from server';
    }
    
    throw new Error(errorMessage);
  }
};

// Enable SMS feature
export const enableSmsFeature = async (organisationId: string) => {
  try {
    const response = await apiClient.post(`/api/sms-billing/enable/${organisationId}`);
    return response.data;
  } catch (error) {
    console.error('Error enabling SMS feature:', error);
    
    // Provide more detailed error information
    let errorMessage = 'Failed to enable SMS';
    
    if (error.response?.status === 403) {
      errorMessage = 'Permission denied: You do not have permission to enable SMS features';
    } else if (error.response?.status === 402) {
      errorMessage = 'Payment required: Please check billing settings';
    } else if (error.response?.status === 429) {
      errorMessage = 'Rate limit exceeded: Please try again later';
    } else if (!navigator.onLine) {
      errorMessage = 'Network error: Please check your internet connection';
    }
    
    throw new Error(errorMessage);
  }
};

// Disable SMS feature
export const disableSmsFeature = async (organisationId: string) => {
  try {
    const response = await apiClient.post(`/api/sms-billing/disable/${organisationId}`);
    return response.data;
  } catch (error) {
    console.error('Error disabling SMS feature:', error);
    
    // Provide more detailed error information
    let errorMessage = 'Failed to disable SMS';
    
    if (error.response?.status === 403) {
      errorMessage = 'Permission denied: You do not have permission to disable SMS features';
    } else if (error.response?.status === 429) {
      errorMessage = 'Rate limit exceeded: Please try again later';
    } else if (!navigator.onLine) {
      errorMessage = 'Network error: Please check your internet connection';
    }
    
    throw new Error(errorMessage);
  }
};

// Get SMS usage
export const getSmsUsage = async (organisationId: string) => {
  try {
    const response = await apiClient.get(`/api/sms-billing/usage/${organisationId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting SMS usage:', error);
    
    // Provide more detailed error information
    let errorMessage = 'Failed to fetch SMS usage data';
    
    if (error.response?.status === 403) {
      errorMessage = 'Permission denied: You do not have access to SMS usage data';
    } else if (error.response?.status === 429) {
      errorMessage = 'Rate limit exceeded: Please try again later';
    } else if (!navigator.onLine) {
      errorMessage = 'Network error: Please check your internet connection';
    }
    
    throw new Error(errorMessage);
  }
};

// Retry helper function with exponential backoff
export const retryWithBackoff = async (fn: () => Promise<any>, maxRetries = 3) => {
  let retries = 0;
  let lastError;
  
  while (retries < maxRetries) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      console.log(`Retry ${retries + 1}/${maxRetries} failed:`, error);
      
      // Skip retry for certain error types
      if (error.response?.status === 403 || // Permission errors
          error.response?.status === 404) { // Not found errors
        break;
      }
      
      // Exponential backoff
      const delay = Math.min(1000 * Math.pow(2, retries), 10000);
      await new Promise(resolve => setTimeout(resolve, delay));
      retries++;
    }
  }
  
  throw lastError;
};

// Function to toggle a customer's subscription status with advanced retry logic and direct endpoint fallback
export const toggleSubscriptionStatus = async (customerId: string, newStatus: boolean, restaurantId: string) => {
  // Define all variables at the beginning to avoid TDZ issues
  let responseData = null;
  let retryCount = 0;
  const maxRetries = 3;
  const startTime = Date.now();
  const operationId = `toggle_sub_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  
  // Enhanced logging for debugging and tracing
  console.log(`[SMS-SUBSCRIPTION] Operation ${operationId}: Toggling subscription for customer ${customerId} to ${newStatus ? 'subscribed' : 'unsubscribed'}`);
  
  // Input validation before attempting any network requests
  if (!customerId) {
    throw new Error('Customer ID is required');
  }
  
  if (typeof newStatus !== 'boolean') {
    throw new Error('Subscription status must be a boolean value');
  }
  
  if (!restaurantId) {
    throw new Error('Restaurant ID is required');
  }
  
  // Define the standard operation - will be tried first
  const performStandardOperation = async () => {
    // Track retry attempts
    if (retryCount > 0) {
      console.log(`[SMS-SUBSCRIPTION] Retry attempt ${retryCount}/${maxRetries} for operation ${operationId}`);
    }
    
    try {
      // Add timeout for the request
      const response = await apiClient.post('/api/sms/customer/toggle-subscription', {
        customerId,
        newStatus,
        restaurantId,
        operationId, // Pass the operation ID for server-side tracking
        scope: 'restaurant-only' // Explicitly set scope to only affect this restaurant
      }, { 
        timeout: 15000 // 15 second timeout
      });
      
      // Store response data safely with validation
      if (!response.data) {
        throw new Error('Empty response received from server');
      }
      
      responseData = response.data;
      
      // Even if server returns success: false, we don't retry - that's a logical failure, not a connectivity issue
      return responseData;
    } catch (requestError) {
      // Enhanced error categorization for better retry decisions
      console.error(`[SMS-SUBSCRIPTION] Error in standard operation ${operationId}:`, requestError);
      
      // Classify errors to determine if retry is appropriate
      let errorMessage = 'Failed to update subscription status';
      let shouldRetry = true;
      let shouldTryDirectEndpoint = false;
      
      // Check for HTTP status codes
      if (requestError.response) {
        // Server responded with error status
        const status = requestError.response.status;
        
        // Map status codes to friendly messages
        if (status === 400) {
          errorMessage = 'Invalid request: Check the customer and restaurant IDs';
          shouldRetry = false; // Don't retry bad requests
        } else if (status === 401 || status === 403) {
          errorMessage = 'Permission denied: You do not have access to update this subscription';
          shouldRetry = false; // Don't retry auth errors
          shouldTryDirectEndpoint = true; // Try direct endpoint for permission issues
        } else if (status === 404) {
          errorMessage = 'Customer not found: The specified customer does not exist';
          shouldRetry = false; // Don't retry if resource doesn't exist
        } else if (status === 207) {
          // Partial success - send back the partial result but flag for direct endpoint
          shouldTryDirectEndpoint = true;
          return requestError.response.data;
        } else if (status === 429) {
          errorMessage = 'Rate limit exceeded: Please try again later';
          shouldRetry = true; // Retry rate limit errors with backoff
        } else if (status >= 500) {
          errorMessage = 'Server error: The subscription service is temporarily unavailable';
          shouldRetry = true; // Retry server errors
          shouldTryDirectEndpoint = true; // Also try direct endpoint for server errors
        }
        
        // Include server details if available
        if (requestError.response.data?.message) {
          errorMessage += ` - ${requestError.response.data.message}`;
        }
      } else if (requestError.request) {
        // No response received - network error
        errorMessage = 'Network error: Could not connect to the subscription service';
        shouldRetry = true; // Retry network errors
      }
      
      // Create an error object with additional metadata
      const enhancedError = new Error(errorMessage);
      (enhancedError as any).originalError = requestError;
      (enhancedError as any).shouldRetry = shouldRetry;
      (enhancedError as any).shouldTryDirectEndpoint = shouldTryDirectEndpoint;
      (enhancedError as any).statusCode = requestError.response?.status;
      (enhancedError as any).operationId = operationId;
      
      throw enhancedError;
    }
  };
  
  // Define the direct operation - will be used as fallback
  const performDirectOperation = async () => {
    console.log(`[SMS-SUBSCRIPTION] Attempting direct endpoint for operation ${operationId}`);
    
    try {
      // Call the dedicated direct endpoint
      const response = await apiClient.post('/api/sms/customer/update-subscription', {
        customerId,
        newStatus,
        restaurantId,
        operationId,
        scope: 'restaurant-only' // Explicitly set scope to only affect this restaurant
      }, { 
        timeout: 20000 // Extended timeout for direct operation
      });
      
      if (!response.data) {
        throw new Error('Empty response from direct endpoint');
      }
      
      return response.data;
    } catch (directError) {
      console.error(`[SMS-SUBSCRIPTION] Error in direct operation ${operationId}:`, directError);
      
      // If we even have a response, return it (partial information is better than none)
      if (directError.response && directError.response.data) {
        return directError.response.data;
      }
      
      throw directError;
    }
  };
  
  // First try the standard endpoint with retries
  try {
    let result;
    let useDirectEndpoint = false;
    
    // Try the standard endpoint first with retries
    while (retryCount < maxRetries && !useDirectEndpoint) {
      try {
        result = await performStandardOperation();
        
        // If we got a successful result, return it
        if (result.success) {
          const duration = Date.now() - startTime;
          console.log(`[SMS-SUBSCRIPTION] Standard operation ${operationId} completed successfully in ${duration}ms`);
          return result;
        }
        
        // If we got a partial success but it wasn't complete, try direct endpoint
        if (result.partial_success) {
          console.log(`[SMS-SUBSCRIPTION] Standard operation ${operationId} had partial success, trying direct endpoint`);
          useDirectEndpoint = true;
          break;
        }
        
        // Result failed but didn't throw - try again with backoff
        retryCount++;
        
        if (retryCount < maxRetries) {
          const baseDelay = Math.min(1000 * Math.pow(1.5, retryCount), 10000);
          const jitter = Math.random() * 1000;
          const delay = baseDelay + jitter;
          
          console.log(`[SMS-SUBSCRIPTION] Waiting ${delay.toFixed(0)}ms before retry ${retryCount} for operation ${operationId}`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error: any) {
        // Check if we should try the direct endpoint based on error type
        if (error.shouldTryDirectEndpoint) {
          console.log(`[SMS-SUBSCRIPTION] Error suggests trying direct endpoint for operation ${operationId}`);
          useDirectEndpoint = true;
          break;
        }
        
        // Check if we should retry based on error type
        if (!error.shouldRetry || retryCount >= maxRetries - 1) {
          // No more retries, but try direct endpoint as last resort
          useDirectEndpoint = true;
          break;
        }
        
        // Increment retry counter
        retryCount++;
        
        // Wait with backoff before retrying
        const baseDelay = Math.min(1000 * Math.pow(1.5, retryCount), 10000);
        const jitter = Math.random() * 1000;
        const delay = baseDelay + jitter;
        
        console.log(`[SMS-SUBSCRIPTION] Waiting ${delay.toFixed(0)}ms before retry ${retryCount} for operation ${operationId}`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // If we got here and should use direct endpoint, try it
    if (useDirectEndpoint) {
      try {
        result = await performDirectOperation();
        const duration = Date.now() - startTime;
        console.log(`[SMS-SUBSCRIPTION] Direct operation ${operationId} completed in ${duration}ms: ${result.success ? 'success' : 'failure'}`);
        return result;
      } catch (directError) {
        // Direct endpoint also failed - throw comprehensive error
        throw directError;
      }
    }
    
    // If we got here, we have a result but it's not successful
    return result;
  } catch (error: any) {
    // Enhanced error logging with complete information
    const duration = Date.now() - startTime;
    console.error(`[SMS-SUBSCRIPTION] Operation ${operationId} failed after ${duration}ms:`, {
      message: error.message,
      retries: retryCount,
      statusCode: error.statusCode,
      customerId,
      newStatus,
      restaurantId
    });
    
    // Include detailed information in the thrown error
    error.operationDetails = {
      operation: 'toggleSubscriptionStatus',
      customerId,
      targetStatus: newStatus,
      restaurantId,
      retryAttempts: retryCount,
      duration
    };
    
    throw error;
  }
};

// Function to get SMS pricing for a restaurant
export const getSmsPricing = async (restaurantId: string) => {
  try {
    const response = await apiClient.get(`/api/sms/pricing?restaurantId=${restaurantId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching SMS pricing:', error);
    
    // Return default pricing if API fails
    return {
      success: true,
      price_per_segment: 0.15,
      currency: 'NZD',
      note: 'Using default pricing due to API error'
    };
  }
};

// Function to get a single campaign by ID
export const getCampaignById = async (campaignId: string, restaurantId?: string) => {
  try {
    // Use direct Supabase query for reliable data access
    const { data: campaign, error } = await supabase
      .from('sms_campaigns')
      .select(`
        id,
        campaign_name,
        status,
        campaign_content,
        schedule_time,
        created_at,
        recipient_count,
        promo_code_id,
        attribution_window_days,
        actual_cost,
        total_estimated_units,
        cost_per_segment,
        restaurant_id,
        organisation_id,
        opt_out_count,
        restaurants_master (
          restaurant_name
        )
      `)
      .eq('id', campaignId)
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    if (!campaign) {
      throw new Error('Campaign not found');
    }

    // Transform to expected UI format
    const transformedCampaign = {
      id: campaign.id.toString(),
      name: campaign.campaign_name,
      status: campaign.status,
      message: campaign.campaign_content || '',
      recipients: campaign.recipient_count || 0,
      delivered: campaign.recipient_count || 0, // Placeholder
      failed: 0, // Placeholder
      opened: 0, // Placeholder  
      clicked: 0, // Placeholder
      optOuts: Number(campaign.opt_out_count) || 0, // Use opt_out_count from campaign
      schedule_time: campaign.schedule_time,
      createdAt: campaign.created_at,
      estimatedCost: 0, // Placeholder
      actualCost: campaign.actual_cost || 0,
      costPerSegment: campaign.cost_per_segment || 0.0079,
      unitsPerMessage: campaign.total_estimated_units || 1,
      promo_code_id: campaign.promo_code_id,
      attribution_window_days: campaign.attribution_window_days,
      restaurant: {
        name: campaign.restaurants_master?.restaurant_name || 'Unknown'
      },
      restaurant_id: campaign.restaurant_id,
      organisation_id: campaign.organisation_id
    };

    return {
      success: true,
      campaign: transformedCampaign
    };

  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

// Function to delete a campaign
export const deleteCampaign = async (campaignId: string, restaurantId: string) => {
  try {
    const response = await apiClient.delete(`/api/sms/campaigns/${campaignId}`, {
      params: { restaurantId }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting campaign:', error);
    
    // In development, mock successful deletion
    if (process.env.NODE_ENV === 'development') {
      console.log('Using mock delete response for development');
      return {
        success: true,
        message: 'Campaign deleted successfully',
        campaignId,
        note: 'This is a mock response for development'
      };
    }
    
    throw error;
  }
};
